import { memo, useCallback } from "react";
import { FormProvider, useForm, UseFormReturn, useWatch } from "react-hook-form";

import { yupResolver } from "@hookform/resolvers/yup";
import MxOverlayLoading from "@rf-l1/loading/mx-overlay-loading/MxOverlayLoading";
import { PropertyBaseContent } from "@rf/property/module/components/property-form/septions/PropertyBaseContent";
import { PROPERTIES_MAIN_OVERLAY_ID } from "./util/const/properties-overlays.const";
import {
  CPropertyForm,
  PropertyFormSchema,
  IPropertyForm,
  CPropertyFormKeys
} from "./util/schemas/property-schema/propertySchema";
import usePropertyDraftV2 from "./hooks/usePropertyDraftV2";
import { useLocation } from "react-router-dom";
import { usePropertyFormFetchFeature } from "./store/slices/FormFeaturesSlice";
import usePropertyCheckConflict from "./hooks/conflict-property/usePropertyCheckConflict";
import { useLoadFormValidationMsgs } from "./store/slices/form-validations-documents-msg/FormValidationsDocumentsMsgSlice";
import useRequiredFields from "@rf-hooks/useRequiredFields";
import useUploadingBlocker from "./hooks/useUploadingBlocker";
import { getPropertyRequiredFields } from "./util/realestate-required-indicator.util";
import useLoadCreatorUser from "./hooks/useLoadCreatorUser";

interface IRealEstateForm {
  state?: IPropertyForm;
  onSubmit: (
    v: IPropertyForm,
    helpers: UseFormReturn<IPropertyForm>
  ) => Promise<any>;
}

const PropertyFormGeneric = (props: IRealEstateForm) => {
  const FORM_CONFIG = useForm<IPropertyForm>({
    defaultValues: { ...CPropertyForm } as unknown as IPropertyForm,
    values: { ...CPropertyForm, ...props.state } as unknown as IPropertyForm,
    mode: "onBlur",
    resolver: yupResolver(PropertyFormSchema)
  });

  const onSubmit = useCallback((v: IPropertyForm) => {
    return props.onSubmit && props.onSubmit(v, FORM_CONFIG);
  }, []);

  const checkKeyDown = (e: any) => {
    if (e.key === "Enter") e.preventDefault();
  };

  return (
    <MxOverlayLoading
      mode="manual"
      id={PROPERTIES_MAIN_OVERLAY_ID}
      loading={FORM_CONFIG.formState.isSubmitting || FORM_CONFIG.formState.isLoading}
      messages={[]}
    >
      <FormProvider {...FORM_CONFIG}>
        <form
          onSubmit={FORM_CONFIG.handleSubmit(onSubmit)}
          onKeyDown={e => checkKeyDown(e)}
          style={{ width: "100%" }}
        >
          <PropertyBaseContent />
          <PropertyFormSyncHook />
        </form>
      </FormProvider>
    </MxOverlayLoading>
  );
};

export default memo(PropertyFormGeneric);

const PropertyFormSyncHook = memo(() => {
  const { pathname } = useLocation();
  const {} = usePropertyDraftV2();
  usePropertyFormFetchFeature();
  usePropertyCheckConflict();
  useLoadFormValidationMsgs();

  useRequiredFields({
    initialPathName: pathname,
    getRequiredFields: getPropertyRequiredFields
  });

  useLoadCreatorUser();
  useUploadingBlocker();
  return <></>;
});
